import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

window.__EVERBOOK_ADMIN_RESCUE_BUILD__ = "admin-rescue-baddb1cd";
window.__EVERBOOK_ADMIN_RESCUE_COMPONENT__ = "AdminErrorBoundary";

const isAdminRoute = window.location.pathname.startsWith('/admin');

const sanitizeDiagnostic = (value) => String(value || '')
  .replace(/([?&](?:access_token|token|auth|key|secret)=)[^&#\s]+/gi, '$1[redacted]')
  .replace(/\bBearer\s+\S+/gi, 'Bearer [redacted]')
  .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[redacted-jwt]')
  .slice(0, 8000);

const safeSourcePath = (source) => {
  if (!source) return 'unknown';
  try {
    return new URL(source, window.location.origin).pathname;
  } catch {
    return sanitizeDiagnostic(source);
  }
};

const appendAdminFatalDiagnostic = ({
  message,
  stack,
  source,
  line,
  column,
}) => {
  const renderPanel = () => {
    if (!document.body) return;

    let panel = document.getElementById('everbook-admin-fatal-diagnostic');
    if (!panel) {
      panel = document.createElement('section');
      panel.id = 'everbook-admin-fatal-diagnostic';
      panel.setAttribute('role', 'alert');
      Object.assign(panel.style, {
        position: 'fixed',
        zIndex: '2147483647',
        left: '16px',
        right: '16px',
        bottom: '16px',
        maxHeight: '45vh',
        overflow: 'auto',
        padding: '16px',
        border: '1px solid #ef4444',
        borderRadius: '12px',
        background: '#180b0b',
        color: '#fee2e2',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: '12px',
        lineHeight: '1.5',
        whiteSpace: 'pre-wrap',
        boxShadow: '0 18px 50px rgba(0, 0, 0, 0.55)',
      });
      document.body.appendChild(panel);
    }

    panel.textContent = [
      'Admin dashboard error.',
      `Message: ${sanitizeDiagnostic(message) || 'Unknown error'}`,
      `Route: ${window.location.pathname}`,
      `Source: ${safeSourcePath(source)}`,
      `Line/column: ${line || 'unknown'}:${column || 'unknown'}`,
      `Stack: ${sanitizeDiagnostic(stack) || 'Unavailable'}`,
    ].join('\n');
  };

  if (document.body) {
    renderPanel();
  } else {
    window.addEventListener('DOMContentLoaded', renderPanel, { once: true });
  }
};

if (isAdminRoute) {
  window.addEventListener('error', (event) => {
    appendAdminFatalDiagnostic({
      message: event.message,
      stack: event.error?.stack,
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    appendAdminFatalDiagnostic({
      message: event.reason?.message || 'Unhandled promise rejection',
      stack: event.reason?.stack,
      source: event.reason?.fileName,
      line: event.reason?.lineNumber,
      column: event.reason?.columnNumber,
    });
  });
}

// Suppress console output in production to prevent info leakage and improve performance
if (process.env.NODE_ENV === 'production') {
  const noop = () => {};
  console.log = noop;
  console.debug = noop;
  console.info = noop;
  console.warn = noop;
  // console.error is preserved for critical error tracking
}

if ('serviceWorker' in navigator && isAdminRoute) {
  navigator.serviceWorker.getRegistrations()
    .then((registrations) => Promise.all(
      registrations.map((registration) => registration.unregister())
    ))
    .catch(() => {});
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register service worker for PWA / offline support
if ('serviceWorker' in navigator && !isAdminRoute) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}
